import React, { useState } from 'react';
import { Grid, InputAdornment, Link, useMediaQuery, useTheme } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Button, LoadingBounce, Panel, TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { DataGrid } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import { getProfileLimits, getRowCount, isContactLimitRelevant, returnPreAprovalContacts } from 'modules/Billing/utils';
import { getBaseUrl } from 'prefs-and-service-offers';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { DialogAddContactLimitsApprovals } from '../Components/DialogAddContactLimitsApprovals';
import { ActionsCell, DialogEditLimits } from '../Components/DialogEditLimits';
import { ServiceLabel } from '../Components/ServiceLabel';
import { BillingProfileContextData, BillingProfileTypes, BillingServiceTypes } from '../types';

export interface IPreApprovalLimitsComponent {
  action: string;
}

export function PreApprovalLimits(props: IPreApprovalLimitsComponent) {
  const { action } = props;
  const editFlag = action === 'edit' || action === 'create';
  const { getValues, control, watch } = useFormContext<BillingProfileContextData>();
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const isMdOrLarger = useMediaQuery(theme.breakpoints.up('md'));

  const { profile } = getValues();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ers_max_tires = watch('profile.approvals.ers_pre_approvals_limits.maximum_num_of_tires');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const ers_max_wheels = watch('profile.approvals.ers_pre_approvals_limits.maxium_num_of_wheels');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onsite_max_tires = watch('profile.approvals.onsite_pre_approvals_limits.maximum_num_of_tires');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onsite_max_wheels = watch('profile.approvals.onsite_pre_approvals_limits.maxium_num_of_wheels');
  const onsite_max_dollars = watch('profile.approvals.onsite_pre_approvals_limits.maximum_dollar_amount');
  const ers_max_dollars = watch('profile.approvals.ers_pre_approvals_limits.maximum_dollar_amount');
  const contactsWithLimitsEdited = watch('contactsWithLimitsEdited');
  const watchPreApprovalsContacts = watch('preApprovalsContacts');
  const watchIsLoggedLocationAndChildrenLoading = watch('isLoggedLocationAndChildrenLoading');
  const watchIsBillingRequirementsChecksLoading = watch('isBillingRequirementsChecksLoading');

  const noContactAvailableMessage = t('No records to display.');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<{ row: any; index: number } | null>(null);

  const handleEdit = (row: any, index: number) => {
    setEditingContact({ row, index });
  };

  const handleRowPrepared = (e: any) => {
    if (e.rowType === 'data') {
      if (e.data.addedForEdit) {
        e.rowElement.style.backgroundColor = '#efeeee';
      }
    }
  };

  const handleCellPrepared = (e: any) => {
    if (e.rowType === 'data') {
      const profileLimits = getProfileLimits(profile);

      // Check Tires column
      if (e.column.caption === t('Tires')) {
        const val = e.data.num_of_tires || 0;
        if (val <= profileLimits.tires) {
          e.cellElement.style.padding = '0px';
        }
      }

      // Check Wheels column
      if (e.column.caption === t('Wheels')) {
        const val = e.data.num_of_wheels || 0;
        if (val <= profileLimits.wheels) {
          e.cellElement.style.padding = '0px';
        }
      }

      // Check Tire Dollar Amount column
      if (
        e.column.caption === t('Max Dollars for Tire Service') ||
        e.column.caption === t('Max Dollars for Mechanical Onsite')
      ) {
        const val = e.data.tire_dollar_amount || 0;
        if (
          val <= profileLimits.tireAmount &&
          profile?.general_information?.service_type === BillingServiceTypes.TIRE_WHEEL
        ) {
          e.cellElement.style.padding = '0px';
        }
      }

      // Check Mechanical Dollar Amount column
      if (e.column.caption === t('Max Dollars for Mechanical ERS')) {
        const val = e.data.mechanical_dollar_amount || 0;
        if (val <= profileLimits.mechanicalAmount) {
          e.cellElement.style.padding = '0px';
        }
      }
    }
  };
  return (
    <Panel title={t('Pre Approval Limits')} style={{ marginBottom: 15 }}>
      <Grid style={{ width: '100%' }}>
        <Grid container spacing={2} style={{ paddingTop: 5 }}>
          {profile?.general_information?.profile_type !== BillingProfileTypes.onsite && (
            <Grid item xs={12}>
              <Grid container spacing={2} style={{ paddingBottom: isMdOrLarger ? 20 : 0 }}>
                <Grid item xs={12} md={2}>
                  <ServiceLabel
                    service={profile.general_information?.service_type || ''}
                    profile={'ERS'}
                    isMdOrLarger={isMdOrLarger}
                  />
                </Grid>
                {profile?.general_information?.service_type !== BillingServiceTypes.MECHANICAL && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="profile.approvals.ers_pre_approvals_limits.maximum_num_of_tires"
                        control={control}
                        rules={{
                          required: { value: true, message: t('Maximum # of Tires') },
                        }}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="ersMaximumNumOfTires"
                            type="number"
                            required
                            label={t('Maximum # of Tires')}
                            InputProps={{ inputProps: { min: 0 }, readOnly: !editFlag }}
                            variant="standard"
                            value={item.field.value || 0}
                            error={
                              !!item.formState.errors.profile?.approvals?.ers_pre_approvals_limits?.maximum_num_of_tires
                            }
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              item.field.onChange(isNaN(value) ? 0 : value);
                            }}
                            helperText={
                              !!item.formState.errors.profile?.approvals?.ers_pre_approvals_limits?.maximum_num_of_tires
                                ? t('Maximum # of Tires is required').toString()
                                : ''
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="profile.approvals.ers_pre_approvals_limits.maxium_num_of_wheels"
                        control={control}
                        rules={{
                          required: { value: true, message: t('Maximum # of Wheeels') },
                        }}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="ersMaximumNumOfWheels"
                            type="number"
                            required
                            label={t('Maximum # of Wheels')}
                            InputProps={{ inputProps: { min: 0 }, readOnly: !editFlag }}
                            variant="standard"
                            value={item.field.value || 0}
                            error={
                              !!item.formState.errors.profile?.approvals?.ers_pre_approvals_limits?.maxium_num_of_wheels
                            }
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              item.field.onChange(isNaN(value) ? 0 : value);
                            }}
                            helperText={
                              !!item.formState.errors.profile?.approvals?.ers_pre_approvals_limits?.maxium_num_of_wheels
                                ? t('Maximum # of Wheels is required').toString()
                                : ''
                            }
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {profile?.general_information?.service_type === BillingServiceTypes.MECHANICAL && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="profile.approvals.ers_pre_approvals_limits.maximum_dollar_amount"
                        control={control}
                        rules={{
                          required: { value: true, message: t('Maximum Dollar Amount') },
                        }}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="ersMaximumDollarAmount"
                            type="number"
                            required
                            label={t('Maximum Dollar Amount')}
                            InputProps={{
                              inputProps: { min: 0 },
                              readOnly: !editFlag,
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            variant="standard"
                            value={ers_max_dollars || 0}
                            error={
                              !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits
                                ?.maximum_dollar_amount
                            }
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              item.field.onChange(isNaN(value) ? 0 : value);
                            }}
                            helperText={
                              !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits
                                ?.maximum_dollar_amount
                                ? t('Maximum Dollar Amount is required').toString()
                                : ''
                            }
                          />
                        )}
                      />
                    </Grid>
                    {isMdOrLarger && (
                      <>
                        <Grid item xs={12} md={7}></Grid>
                        <Grid item xs={12} md={2}></Grid>
                      </>
                    )}
                    <Grid item xs={12} md={9}>
                      <Controller
                        name="profile.approvals.ers_pre_approvals_limits.additional_notes"
                        control={control}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="comments"
                            label={t('Additional Notes')}
                            type="string"
                            variant="standard"
                            value={item.field.value || ''}
                            onChange={(e) => item.field.onChange(e.target.value)}
                            readOnly={props.action === 'view'}
                            multiline
                            rows={getRowCount(item.field.value || '')}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid
                  item
                  xs={12}
                  md={profile?.general_information?.service_type === BillingServiceTypes.MECHANICAL ? 1 : 4}
                />
              </Grid>
            </Grid>
          )}
          {profile?.general_information?.profile_type !== BillingProfileTypes.ers && (
            <Grid item xs={12} md={12}>
              <Grid container spacing={2} style={{ paddingBottom: isMdOrLarger ? 20 : 0 }}>
                <Grid item xs={12} md={2}>
                  <ServiceLabel
                    service={profile.general_information?.service_type || ''}
                    profile={'On-site'}
                    isMdOrLarger={isMdOrLarger}
                  />
                </Grid>
                {profile?.general_information?.service_type !== BillingServiceTypes.MECHANICAL && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="profile.approvals.onsite_pre_approvals_limits.maximum_num_of_tires"
                        control={control}
                        rules={{
                          required: { value: true, message: t('Maximum # of Tires') },
                        }}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="onsiteMaximumNumOfTires"
                            type="number"
                            required
                            label={t('Maximum # of Tires')}
                            InputProps={{ inputProps: { min: 0 }, readOnly: !editFlag }}
                            variant="standard"
                            value={item.field.value || 0}
                            error={
                              !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits
                                ?.maximum_num_of_tires
                            }
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              item.field.onChange(isNaN(value) ? 0 : value);
                            }}
                            helperText={
                              !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits
                                ?.maximum_num_of_tires
                                ? t('Maximum # of Tires is required').toString()
                                : ''
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="profile.approvals.onsite_pre_approvals_limits.maxium_num_of_wheels"
                        control={control}
                        rules={{
                          required: { value: true, message: t('Maximum # of Wheeels') },
                        }}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="onsiteMaximumNumOfWheels"
                            type="number"
                            required
                            label={t('Maximum # of Wheels')}
                            InputProps={{ inputProps: { min: 0 }, readOnly: !editFlag }}
                            variant="standard"
                            value={item.field.value || 0}
                            error={
                              !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits
                                ?.maxium_num_of_wheels
                            }
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              item.field.onChange(isNaN(value) ? 0 : value);
                            }}
                            helperText={
                              !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits
                                ?.maxium_num_of_wheels
                                ? t('Maximum # of Wheels is required').toString()
                                : ''
                            }
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={3}>
                  <Controller
                    name="profile.approvals.onsite_pre_approvals_limits.maximum_dollar_amount"
                    control={control}
                    rules={{
                      required: { value: true, message: t('Maximum Dollar Amount') },
                    }}
                    render={(item) => (
                      <TextField
                        id={item.field.name}
                        name="onsiteMaximumDollarAmount"
                        type="number"
                        required
                        label={t('Maximum Dollar Amount')}
                        InputProps={{
                          inputProps: { min: 0 },
                          readOnly: !editFlag,
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        variant="standard"
                        value={onsite_max_dollars || 0}
                        error={
                          !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits?.maximum_dollar_amount
                        }
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          item.field.onChange(isNaN(value) ? 0 : value);
                        }}
                        helperText={
                          !!item.formState.errors.profile?.approvals?.onsite_pre_approvals_limits?.maximum_dollar_amount
                            ? t('Maximum Dollar Amount is required').toString()
                            : ''
                        }
                      />
                    )}
                  />
                </Grid>
                {profile?.general_information?.service_type === BillingServiceTypes.MECHANICAL && (
                  <>
                    {isMdOrLarger && (
                      <>
                        <Grid item xs={12} md={7}></Grid>
                        <Grid item xs={12} md={2}></Grid>
                      </>
                    )}
                    <Grid item xs={12} md={9}>
                      <Controller
                        name="profile.approvals.onsite_pre_approvals_limits.additional_notes"
                        control={control}
                        render={(item) => (
                          <TextField
                            id={item.field.name}
                            name="comments"
                            label={t('Additional Notes')}
                            type="string"
                            variant="standard"
                            value={item.field.value || ''}
                            onChange={(e) => item.field.onChange(e.target.value)}
                            readOnly={props.action === 'view'}
                            multiline
                            rows={getRowCount(item.field.value || '')}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={1} />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Panel
          title={
            profile && profile?.general_information?.service_type === BillingServiceTypes.MECHANICAL
              ? t('Authorized Approvers of Mechanical Services')
              : t('Authorized Tire and Wheel Approvers')
          }
          subtitle={t('above Pre-Approval Limits')}
          control={
            !editFlag || watchIsLoggedLocationAndChildrenLoading ? (
              <></>
            ) : (
              <Button
                color="primary"
                size="small"
                style={{ marginTop: 4 }}
                onClick={() => {
                  setOpenDialog(true);
                }}
                disabled={watchIsBillingRequirementsChecksLoading}
              >
                {t('Add')}
              </Button>
            )
          }
        >
          {watchIsBillingRequirementsChecksLoading ? (
            <LoadingBounce style={{ height: 300 }} />
          ) : (
            <DataGrid
              showBorders
              className="freespaced-table"
              noDataText={noContactAvailableMessage}
              dataSource={
                watchIsLoggedLocationAndChildrenLoading
                  ? []
                  : contactsWithLimitsEdited
                      .filter((x) => !x.deleted)
                      .concat(
                        watchPreApprovalsContacts.filter((row) => {
                          if (typeof row === 'undefined' || row === null) return false;
                          const isEdited = contactsWithLimitsEdited.some((edited) => edited.id === row.id);
                          if (isEdited) return false;

                          return returnPreAprovalContacts(profile, row, true, contactsWithLimitsEdited);
                        }),
                      )
              }
              style={{ maxHeight: 440 }}
              showColumnHeaders
              scrolling={{ mode: 'virtual' }}
              onRowPrepared={handleRowPrepared}
              onCellPrepared={handleCellPrepared}
              wordWrapEnabled
            >
              <Column
                caption={t('Full Name')}
                allowFiltering={false}
                cellRender={({ data: row }: any) => {
                  return editFlag ? (
                    <span
                      style={{
                        fontWeight: 500,
                      }}
                    >
                      {row.addedForEdit && (
                        <span
                          style={{
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff',
                            borderRadius: 4,
                            padding: '0px 4px',
                            marginRight: 8,
                            fontSize: '0.7em',
                            display: 'inline-block',
                            verticalAlign: 'text-top',
                          }}
                        >
                          {t('New')}
                        </span>
                      )}
                      <b>
                        {row.first_name} {row.last_name}
                      </b>
                    </span>
                  ) : (
                    <Link
                      style={{
                        textDecoration: 'underline',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                      onClick={() => history.push(`${getBaseUrl()}/contacts/${row.id}/view`)}
                    >
                      <b>
                        {row.first_name} {row.last_name}
                      </b>
                    </Link>
                  );
                }}
              />
              <Column
                caption={t('Tires')}
                allowFiltering={false}
                cellRender={({ data: row, rowIndex }: any) => {
                  const limit = getProfileLimits(profile).tires;
                  const val = row.num_of_tires || 0;
                  const isLow = val <= limit;
                  if (isLow && !isContactLimitRelevant(profile, row)) {
                    return (
                      <Alert
                        style={{
                          padding: '2px',
                          fontSize: '0.8em',
                          alignItems: 'center',
                        }}
                        severity="error"
                        icon={false}
                        action={
                          <Button
                            onClick={() => {
                              handleEdit(row, rowIndex);
                            }}
                            size="small"
                            style={{
                              fontSize: '0.9em',
                              padding: '2px',
                              minWidth: 'unset',
                              marginRight: '12px',
                              marginLeft: '-12px',
                            }}
                          >
                            {t('Fix')}
                          </Button>
                        }
                      >
                        {t(
                          'Contact cannot be added because the approval limit is lower than the general Tires limit ({{limit}})',
                          {
                            limit,
                          },
                        )}
                      </Alert>
                    );
                  }
                  return <span>{val || ''}</span>;
                }}
                visible={profile?.general_information?.service_type === BillingServiceTypes.TIRE_WHEEL}
              />
              <Column
                caption={t('Wheels')}
                allowFiltering={false}
                cellRender={({ data: row, rowIndex }: any) => {
                  const limit = getProfileLimits(profile).wheels;
                  const val = row.num_of_wheels || 0;
                  const isLow = val <= limit;
                  if (isLow && !isContactLimitRelevant(profile, row)) {
                    return (
                      <Alert
                        style={{
                          padding: '2px',
                          fontSize: '0.8em',
                          alignItems: 'center',
                        }}
                        severity="error"
                        icon={false}
                        action={
                          <Button
                            onClick={() => {
                              handleEdit(row, rowIndex);
                            }}
                            size="small"
                            style={{
                              fontSize: '0.9em',
                              padding: '2px',
                              minWidth: 'unset',
                              marginRight: '12px',
                              marginLeft: '-12px',
                            }}
                          >
                            {t('Fix')}
                          </Button>
                        }
                      >
                        {t(
                          'Contact cannot be added because the approval limit is lower than the general Wheels limit ({{limit}})',
                          {
                            limit,
                          },
                        )}
                      </Alert>
                    );
                  }
                  return <span>{val || ''}</span>;
                }}
                visible={profile?.general_information?.service_type === BillingServiceTypes.TIRE_WHEEL}
              />
              <Column
                visible={profile?.general_information?.service_type === BillingServiceTypes.MECHANICAL}
                caption={t('Max Dollars for Mechanical ERS')}
                allowFiltering={false}
                cellRender={({ data: row, rowIndex }: any) => {
                  const limit = getProfileLimits(profile).mechanicalAmount;
                  const val = row.mechanical_dollar_amount || 0;
                  const isLow = val <= limit;
                  if (isLow && !isContactLimitRelevant(profile, row)) {
                    return (
                      <Alert
                        style={{
                          padding: '2px',
                          fontSize: '0.8em',
                          alignItems: 'center',
                        }}
                        severity="error"
                        icon={false}
                        action={
                          <Button
                            onClick={() => {
                              handleEdit(row, rowIndex);
                            }}
                            size="small"
                            style={{
                              fontSize: '0.9em',
                              padding: '2px',
                              minWidth: 'unset',
                              marginRight: '10px',
                              marginLeft: '-14px',
                            }}
                          >
                            {t('Fix')}
                          </Button>
                        }
                      >
                        {t(
                          'Contact cannot be added because the approval limit is lower than the general Dollar Amt limit ({{limit}})',
                          {
                            limit,
                          },
                        )}
                      </Alert>
                    );
                  }
                  return <span>{val ? `$ ${val}` : ''}</span>;
                }}
              />
              <Column
                visible={profile?.general_information?.profile_type !== BillingProfileTypes.ers}
                caption={
                  profile?.general_information?.service_type === BillingServiceTypes.TIRE_WHEEL
                    ? t('Max Dollars for Tire Service')
                    : t('Max Dollars for Mechanical Onsite')
                }
                allowFiltering={false}
                cellRender={({ data: row, rowIndex }: any) => {
                  const limit = getProfileLimits(profile).tireAmount;
                  const val = row.tire_dollar_amount || 0;
                  const isLow = val <= limit;
                  if (
                    isLow &&
                    !isContactLimitRelevant(profile, row) &&
                    ((profile?.general_information?.service_type === BillingServiceTypes.TIRE_WHEEL &&
                      (profile?.general_information?.profile_type === BillingProfileTypes.onsite ||
                        profile?.general_information?.profile_type === BillingProfileTypes.ers_onsite)) ||
                      profile?.general_information?.service_type === BillingServiceTypes.MECHANICAL)
                  ) {
                    return (
                      <Alert
                        style={{
                          padding: '2px',
                          fontSize: '0.8em',
                          alignItems: 'center',
                        }}
                        severity="error"
                        icon={false}
                        action={
                          <Button
                            onClick={() => {
                              handleEdit(row, rowIndex);
                            }}
                            size="small"
                            style={{
                              fontSize: '0.9em',
                              padding: '2px',
                              minWidth: 'unset',
                              marginRight: '12px',
                              marginLeft: '-12px',
                            }}
                          >
                            {t('Fix')}
                          </Button>
                        }
                      >
                        {t(
                          'Contact cannot be added because the approval limit is lower than the general Dollar Amt limit ({{limit}})',
                          {
                            limit,
                          },
                        )}
                      </Alert>
                    );
                  }
                  return <span>{val ? `$ ${val}` : ''}</span>;
                }}
              />
              <Column
                caption={t('Work Phone')}
                allowFiltering={false}
                cellRender={(row: any) => (row.data.work_phone ? row.data.work_phone : '-')}
              />
              <Column
                caption={t('Cell Phone')}
                allowFiltering={false}
                cellRender={(row: any) => (row.data.cell_phone ? row.data.cell_phone : '-')}
              />
              <Column
                caption={t('Email Address')}
                allowFiltering={false}
                cellRender={(row: any) => (row.data.email_address ? row.data.email_address : '-')}
              />
              <Column
                caption=""
                allowFiltering={false}
                cellRender={({ data: row, rowIndex }: any) =>
                  editFlag ? <ActionsCell row={row} rowIndex={rowIndex} onEdit={handleEdit} /> : null
                }
              />
            </DataGrid>
          )}
        </Panel>
      </Grid>
      <DialogAddContactLimitsApprovals openDialog={openDialog} closeDialog={() => setOpenDialog(false)} />
      {editingContact && (
        <DialogEditLimits
          openDialog={!!editingContact}
          closeDialog={() => setEditingContact(null)}
          row={editingContact.row}
          rowIndex={editingContact.index}
        />
      )}
    </Panel>
  );
}
